import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Volume2, VolumeX, Gem } from "lucide-react";
import { FRIDGE_VIDEO } from "@/lib/assets";
import { useAppLaunch } from "@/components/landing/AppLaunch";
import { useLang } from "@/i18n/LanguageContext";

export const ScanFridge = () => {
  const { t } = useLang();
  const { launchApp } = useAppLaunch();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [musicOn, setMusicOn] = useState(false);

  const toggleMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.volume = 0.5;
      a.play().catch(() => {});
      setMusicOn(true);
    } else {
      a.pause();
      setMusicOn(false);
    }
  };

  return (
    <section id="demo" data-testid="scan-fridge-section" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 grid-dots opacity-40" />
      <div className="absolute top-1/2 -translate-y-1/2 -left-40 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />
            {t.scanFridge.overline}
          </span>
          <h2 className="mt-6 font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            {t.scanFridge.title}
          </h2>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-400">
            {t.scanFridge.subtitle}
          </p>

          <ul className="mt-8 space-y-3">
            {t.scanFridge.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-200">
                <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full bg-emerald-400 text-emerald-950">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={launchApp}
            data-testid="scan-fridge-cta"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-8 py-4 text-sm font-bold uppercase tracking-wider text-emerald-950 hover:bg-emerald-300 transition-colors duration-300 shadow-lg shadow-emerald-500/25"
          >
            {t.scanFridge.cta}
          </button>

          <div
            data-testid="scan-fridge-premium"
            className="mt-6 flex items-start gap-3 rounded-2xl border border-violet-400/25 bg-violet-500/[0.08] p-4"
          >
            <span className="grid place-items-center h-8 w-8 shrink-0 rounded-lg bg-violet-400/15 text-violet-300 ring-1 ring-violet-400/30">
              <Gem className="h-4 w-4" />
            </span>
            <p className="text-sm text-violet-200 leading-relaxed">
              {t.scanFridge.premium}
            </p>
          </div>
        </div>

        <div className="relative flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 -z-10 rounded-[3rem] bg-emerald-500/20 blur-3xl" />
            <div className="relative rounded-[2.4rem] border border-white/10 bg-black p-2 shadow-2xl">
              <div className="overflow-hidden rounded-[1.9rem]">
                <video
                  ref={videoRef}
                  src={FRIDGE_VIDEO}
                  data-testid="scan-fridge-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="block w-[260px] sm:w-[300px] bg-black"
                />
              </div>
              <audio ref={audioRef} src={`${process.env.PUBLIC_URL}/audio/demo-music.mp3`} loop preload="none" />
              <button
                type="button"
                onClick={toggleMusic}
                data-testid="scan-fridge-mute"
                aria-label="Musique"
                className="absolute bottom-5 right-5 grid place-items-center h-11 w-11 rounded-full bg-emerald-400 text-emerald-950 shadow-lg hover:bg-emerald-300 transition-colors"
              >
                {musicOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <p className="relative mt-8 text-center text-xs text-slate-500">
        Musique : « Volatile Reaction » — Kevin MacLeod (incompetech.com) · CC BY 4.0
      </p>
    </section>
  );
};
